"use client";

import SiteAnchor from "@/components/ui/SiteAnchor";

import Image from "next/image";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  ArrowRight,
  Briefcase,
  Check,
  ClipboardList,
  GraduationCap,
  Layers,
  Megaphone,
  Plane,
  Pause,
  Play,
  ShieldCheck,
  Star,
  User,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { reactWebAuthHref } from "@/lib/reactWebAuthUrl";
import type {
  HomeHeroAgent,
  HomeHeroChipTone,
  HomeHeroCluster,
  HomeHeroIconKey,
  HomeHeroPanel,
  HomeHeroPanelRow,
} from "@/types/home-messages";

import { AgentCarousel } from "./agentCarousel";

const NAVY = "#1e3a5f";
const ORANGE = "#FF6B35";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onStoreChange: () => void): () => void {
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function reducedMotionSnapshot(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function reducedMotionServerSnapshot(): boolean {
  return false;
}

const iconMap: Record<HomeHeroIconKey, LucideIcon> = {
  plane: Plane,
  users: Users,
  briefcase: Briefcase,
  user: User,
  clipboard: ClipboardList,
  megaphone: Megaphone,
  shield: ShieldCheck,
  wrench: Wrench,
  graduation: GraduationCap,
  star: Star,
  layers: Layers,
};

const chipStyles: Record<HomeHeroChipTone, { bg: string; fg: string }> = {
  green: { bg: "#dcfce7", fg: "#15803d" },
  amber: { bg: "#fef3c7", fg: "#b45309" },
  red: { bg: "#fee2e2", fg: "#b91c1c" },
  blue: { bg: "#dbeafe", fg: "#1d4ed8" },
  navy: { bg: "#e0e7ef", fg: NAVY },
  gray: { bg: "#f1f5f9", fg: "#475569" },
};

function Chip({
  tone,
  children,
}: {
  readonly tone: HomeHeroChipTone;
  readonly children: string;
}) {
  const styles = chipStyles[tone];

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 landing-text-detail font-semibold"
      style={{ backgroundColor: styles.bg, color: styles.fg }}
    >
      {children}
    </span>
  );
}

function ProgressBar({
  pct,
  color = "#22c55e",
}: {
  readonly pct: number;
  readonly color?: string;
}) {
  const safePct = Math.max(0, Math.min(100, pct));

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full"
        style={{ width: `${safePct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function PanelRow({ row }: { readonly row: HomeHeroPanelRow }) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2 landing-text-detail">
        <span className="flex min-w-0 items-center gap-2 text-gray-800">
          {row.checked ? (
            <Check
              className="h-3 w-3 shrink-0 text-green-600"
              strokeWidth={3}
              aria-hidden
            />
          ) : null}
          <span className="break-words">{row.label}</span>
        </span>
        {row.chip && row.tone ? <Chip tone={row.tone}>{row.chip}</Chip> : null}
        {row.value ? (
          <span className="shrink-0 font-medium text-gray-900">
            {row.value}
          </span>
        ) : null}
        {row.arrow ? (
          <ArrowRight
            className="h-3 w-3 shrink-0"
            style={{ color: ORANGE }}
            aria-hidden
          />
        ) : null}
      </div>
      {typeof row.progress === "number" ? (
        <ProgressBar pct={row.progress} color={row.progressColor} />
      ) : null}
    </div>
  );
}

function AgentPanel({ panel }: { readonly panel: HomeHeroPanel }) {
  return (
    <div className="landing-hero-agent-panel rounded-lg border border-gray-200 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
      <div
        className="mb-2 landing-text-detail font-bold uppercase tracking-normal"
        style={{ color: NAVY }}
      >
        {panel.title}
      </div>

      {panel.profile ? (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full landing-text-detail font-bold text-white"
              style={{ backgroundColor: NAVY }}
            >
              {panel.profile.initials}
            </div>
            <div className="min-w-0">
              <div
                className="break-words landing-text-detail font-bold"
                style={{ color: NAVY }}
              >
                {panel.profile.name}
              </div>
              <div className="break-words landing-text-detail text-gray-500">
                {panel.profile.role}
              </div>
            </div>
          </div>
          <div className="mb-1 flex items-center justify-between gap-2 landing-text-detail">
            <span className="text-gray-600">
              {panel.profile.completionLabel}
            </span>
            <span className="font-semibold text-green-700">
              {panel.profile.completionValue}
            </span>
          </div>
          <ProgressBar pct={panel.profile.completionProgress} />
        </>
      ) : null}

      {panel.media ? (
        <div className="mb-2">
          <div className="landing-hero-agent-media mb-2 flex h-16 items-center justify-center overflow-hidden rounded-md bg-slate-800">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow"
              aria-label={panel.media.playLabel}
            >
              <Play
                className="ml-0.5 h-3.5 w-3.5"
                style={{ color: ORANGE }}
                fill={ORANGE}
                aria-hidden
              />
            </button>
          </div>
          <div
            className="landing-text-detail font-semibold"
            style={{ color: NAVY }}
          >
            {panel.media.title}
          </div>
          <div className="landing-text-detail text-gray-500">
            {panel.media.duration}
          </div>
        </div>
      ) : null}

      {panel.metric ? (
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-[18px] font-bold" style={{ color: NAVY }}>
            {panel.metric.value}
          </span>
          <Chip tone={panel.metric.tone}>{panel.metric.chip}</Chip>
        </div>
      ) : null}

      {panel.rows?.length ? (
        <div className="landing-hero-panel-rows space-y-2">
          {panel.rows.map((row) => (
            <PanelRow key={`${panel.title}-${row.label}`} row={row} />
          ))}
        </div>
      ) : null}

      {panel.footnote ? (
        <div className="pt-1 landing-text-detail text-gray-500">
          {panel.footnote}
        </div>
      ) : null}
    </div>
  );
}

const inactivePlaybackSnapshot = () => null;

function AgentSelector({
  carousel, reduced, agent, index, isActive,
}: {
  carousel: AgentCarousel;
  reduced: boolean;
  agent: HomeHeroAgent;
  index: number;
  isActive: boolean;
}) {
  const t = useTranslations("Hero");
  // Only the active icon repaints with the clock. Other icons keep their
  // ordinary agent identity until the user hovers or focuses them.
  const playback = useSyncExternalStore(
    carousel.subscribe,
    isActive ? carousel.getSnapshot : inactivePlaybackSnapshot,
    isActive ? carousel.getSnapshot : inactivePlaybackSnapshot,
  );
  const fraction = playback ? Math.max(0, Math.min(1, playback.remainingMs / playback.durationMs)) : 1;
  const progress = reduced ? Math.ceil(fraction * 12) / 12 : fraction;
  const canPlay = !isActive || !!playback?.canResume;
  const PlaybackIcon = canPlay ? Play : Pause;
  const RailIcon = iconMap[agent.icon as HomeHeroIconKey] ?? ClipboardList;
  return (
    <button
      type="button"
      className="landing-hero-agent-selector group flex flex-1 flex-col items-center gap-1.5 focus:outline-none"
      aria-label={`${agent.short} ${agent.name} — ${t(canPlay ? "resumeRotation" : "pauseRotation")}`}
      aria-pressed={isActive}
      data-playback={playback?.mode}
      data-playback-action={canPlay ? "play" : "pause"}
      data-remaining-ms={playback ? Math.round(playback.remainingMs) : undefined}
      onClick={() => {
        if (!isActive) {
          carousel.select(index);
          carousel.play();
        } else if (canPlay) carousel.play();
        else carousel.pause();
      }}
    >
      <div className="landing-hero-agent-selector-icon flex h-12 w-12 items-center justify-center rounded-full bg-white">
        {isActive ? (
          <svg className="landing-hero-agent-selector-ring" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
            <circle className="landing-hero-countdown-track" cx="24" cy="24" r="22.5" />
            <circle className="landing-hero-countdown-progress" cx="24" cy="24" r="22.5"
              pathLength="100" strokeDasharray="100 100" vectorEffect="non-scaling-stroke"
              strokeDashoffset={-(1 - progress) * 100} transform="rotate(-90 24 24)" />
          </svg>
        ) : null}
        <RailIcon className="landing-hero-agent-identity h-5 w-5" aria-hidden="true" />
        <PlaybackIcon className="landing-hero-agent-icon-action h-4 w-4" fill="currentColor" aria-hidden="true" />
      </div>
      <span className="text-center">
        <span className="block landing-text-detail font-bold leading-tight" style={{ color: NAVY }}>
          {agent.short}
        </span>
        <span className="mx-auto block max-w-[130px] landing-text-detail leading-tight text-gray-500">
          {agent.name}
        </span>
      </span>
    </button>
  );
}

export default function HeroSection() {
  const t = useTranslations("Hero");
  const locale = useLocale();
  const [carousel] = useState(() => new AgentCarousel({
    now: () => performance.now(),
    request: (callback) => requestAnimationFrame(callback),
    cancel: (id) => cancelAnimationFrame(id),
  }));
  // Only the active selector subscribes to animation frames; hero content renders
  // when the agent index changes, not on every ring update.
  const active = useSyncExternalStore(carousel.subscribe, carousel.getIndex, carousel.getIndex);
  const reduced = useSyncExternalStore(
    subscribeToReducedMotion,
    reducedMotionSnapshot,
    reducedMotionServerSnapshot,
  );
  const showcaseRef = useRef<HTMLDivElement | null>(null);
  const keyboardInput = useRef(false);
  const heroRef = useRef<HTMLElement | null>(null);

  const clusters = (t.raw("clusters") as HomeHeroCluster[]) ?? [];
  const agents = (t.raw("agents") as HomeHeroAgent[]) ?? [];

  useEffect(() => {
    const hero = heroRef.current;
    const header = document.querySelector<HTMLElement>(".site-header");
    if (!hero || !header) return;
    // The responsive header changes height with logo size, wrapping and locale.
    const measureHeader = () => hero.style.setProperty(
      "--landing-header-height", `${header.getBoundingClientRect().height}px`,
    );
    measureHeader();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureHeader);
    observer.observe(header);
    return () => observer.disconnect();
  }, [agents.length]);

  useEffect(() => {
    carousel.configure(agents.length);
    carousel.setHold("reduced", reduced);
  }, [carousel, agents.length, reduced]);

  useEffect(() => {
    const showcase = showcaseRef.current;
    const visibility = () => carousel.setHold("hidden", document.hidden);
    const keydown = () => { keyboardInput.current = true; };
    const pointerdown = () => { keyboardInput.current = false; };
    visibility();
    document.addEventListener("visibilitychange", visibility);
    document.addEventListener("keydown", keydown, true);
    document.addEventListener("pointerdown", pointerdown, true);
    carousel.setHold("offscreen", !!showcase && typeof IntersectionObserver !== "undefined");
    const observer = showcase && typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(([entry]) => carousel.setHold("offscreen", !entry.isIntersecting), { threshold: 0 })
      : null;
    if (showcase) observer?.observe(showcase);
    carousel.start();
    return () => {
      carousel.stop();
      observer?.disconnect();
      document.removeEventListener("visibilitychange", visibility);
      document.removeEventListener("keydown", keydown, true);
      document.removeEventListener("pointerdown", pointerdown, true);
    };
  }, [carousel]);

  if (agents.length === 0) return null;

  const currentIndex = agents[active] ? active : 0;
  const agent = agents[currentIndex];
  const AgentIcon = iconMap[agent.icon as HomeHeroIconKey] ?? ClipboardList;
  const signupHref = reactWebAuthHref(locale, "signup");


  return (
    <section ref={heroRef} className="landing-hero relative z-10 bg-gradient-to-b from-white via-slate-50/40 to-white px-4 pb-4 pt-8 sm:px-6 lg:px-8">
      <div className="relative mx-auto max-w-[90rem]">
        <div className="landing-hero-drone pointer-events-none absolute left-[40%] top-0 z-30 hidden -translate-x-1/2 xl:block">
          <motion.div
            animate={reduced ? undefined : { y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Image
              src={t("droneImageSrc")}
              alt={t("droneImageAlt")}
              width={208}
              height={139}
              className="h-auto w-[208px] select-none"
              priority
            />
          </motion.div>
        </div>

        <svg
          className="landing-hero-flight-path pointer-events-none absolute left-[36%] top-[50px] z-40 hidden h-[280px] w-[16%] xl:block"
          viewBox="0 0 200 280"
          fill="none"
          aria-hidden
          preserveAspectRatio="none"
        >
          <path
            d="M 2 278 C 40 280 80 275 110 250 C 128 232 130 210 126 190 C 122 140 95 130 80 148 C 65 166 85 190 112 178 C 145 165 180 140 192 100 C 200 70 195 45 160 16"
            stroke={ORANGE}
            strokeWidth="2.5"
            strokeDasharray="6 7"
            strokeLinecap="round"
          />
        </svg>

        <div className="landing-hero-grid grid grid-cols-1 items-start gap-8 xl:grid-cols-[1.05fr_1fr] xl:gap-10">
          <div className="landing-hero-message">
            <div className="landing-hero-eyebrow inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 landing-text-detail text-gray-700 shadow-sm">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: ORANGE }}
                aria-hidden
              />
              {t("eyebrow")}
            </div>

            <h1
              className="landing-hero-title mt-4 text-[40px] font-bold leading-[0.95] tracking-normal min-[380px]:text-[48px] sm:text-[64px] lg:text-[76px]"
              style={{ color: NAVY }}
            >
              <span className="block">{t("titlePlan")}</span>
              <span className="block">{t("titleTrack")}</span>
              <span className="block">{t("titleFly")}</span>
              <span className="block break-words" style={{ color: ORANGE }}>
                {t("titleAgents")}
              </span>
            </h1>

            <p className="landing-hero-intro mt-5 max-w-2xl landing-text-lead text-gray-600">
              {t("intro")}
            </p>

            <div className="landing-hero-actions mt-6 flex flex-wrap items-center gap-5">
              <div className="relative">
                <div
                  className="absolute -inset-0.5 rounded-lg opacity-70 blur-sm"
                  style={{ backgroundColor: ORANGE }}
                  aria-hidden
                />
                <Button
                  asChild
                  className="relative min-h-12 h-auto border-0 px-6 py-3 landing-text-body text-white hover:opacity-95"
                  style={{ backgroundColor: NAVY }}
                >
                  <SiteAnchor href={signupHref}>
                    {t("primaryCta")}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </SiteAnchor>
                </Button>
              </div>
              <Link
                href={t("businessHref")}
                className="flex items-center gap-2 landing-text-body font-medium hover:underline"
                style={{ color: ORANGE }}
              >
                {t("businessCta")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>


            <div className="landing-hero-features landing-compact-card-copy mt-5 grid grid-cols-1 gap-3 min-[380px]:grid-cols-2">
              {clusters.map((cluster) => {
                const ClusterIcon = iconMap[cluster.icon] ?? Plane;
                return (
                  <div
                    key={cluster.label}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="mb-2 flex items-center gap-1.5">
                      <ClusterIcon
                        className="h-3.5 w-3.5"
                        style={{ color: ORANGE }}
                        aria-hidden
                      />
                      <span
                        className="landing-text-detail font-semibold uppercase tracking-normal"
                        style={{ color: NAVY }}
                      >
                        {cluster.label}
                      </span>
                    </div>
                    <ul className="space-y-1 pl-0">
                      {cluster.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 landing-text-body text-gray-700"
                        >
                          <Check
                            className="mt-0.5 h-3 w-3 shrink-0"
                            style={{ color: ORANGE }}
                            strokeWidth={3}
                            aria-hidden
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <div ref={showcaseRef} className="landing-hero-showcase relative"
            onFocusCapture={(event) => {
              if (keyboardInput.current || event.target.matches(":focus-visible")) carousel.setHold("keyboard", true);
            }}
            onKeyDownCapture={(event) => {
              if (event.key === "Tab" || event.key.startsWith("Arrow")) carousel.setHold("keyboard", true);
            }}
          >
            <div
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) carousel.setHold("hover", true);
              }}
              onPointerLeave={() => carousel.setHold("hover", false)}
              className="landing-hero-agent-card landing-compact-card-copy relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.12)]"
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={agent.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.45 }}
                  data-agent={agent.key}
                  className="landing-hero-agent-content relative p-5 sm:p-6"
                >
                  <div className="landing-hero-agent-heading flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-md"
                        style={{ backgroundColor: NAVY }}
                      >
                        <AgentIcon className="h-5 w-5 text-white" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-[26px] font-bold leading-tight"
                          style={{ color: NAVY }}
                        >
                          {agent.short}
                        </div>
                        <div
                          className="mt-1 landing-text-body font-semibold"
                          style={{ color: ORANGE }}
                        >
                          {agent.name}
                        </div>
                      </div>
                    </div>
                    <Chip tone={agent.badgeTone}>{agent.badgeText}</Chip>
                  </div>

                  <div className="landing-hero-agent-body mt-4 grid gap-4 sm:grid-cols-[1.2fr_1fr]">
                    <div className="landing-hero-agent-copy min-w-0">
                      <p className="landing-text-body leading-relaxed text-gray-700">
                        {agent.description}
                      </p>
                      <ul className="landing-hero-agent-checks mt-4 space-y-2 pl-0">
                        {agent.checks.map((check) => (
                          <li
                            key={check}
                            className="flex items-start gap-2 landing-text-body text-gray-800"
                          >
                            <Check
                              className="mt-1 h-4 w-4 shrink-0"
                              style={{ color: ORANGE }}
                              strokeWidth={3}
                              aria-hidden
                            />
                            <span>{check}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="landing-hero-agent-portrait">
                      <img
                        src={agent.imageSrc}
                        alt={agent.imageAlt}
                        className="landing-hero-agent-image mx-auto h-64 w-full max-w-xs select-none object-contain object-bottom sm:h-full sm:max-h-96"
                        draggable={false}
                      />
                    </div>
                  </div>

                  <div className="landing-hero-agent-panels mt-5 grid items-end gap-3 sm:grid-cols-2">
                    {agent.panels.slice(0, 2).map((panel) => (
                      <AgentPanel
                        key={`${agent.key}-${panel.title}`}
                        panel={panel}
                      />
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="landing-hero-agent-rail mt-5">
              <p className="mb-3 text-center landing-text-detail text-gray-500">
                {t("agentRailLabel")}
              </p>
              <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:grid-cols-5">
                {agents.map((item, index) => (
                  <AgentSelector key={item.key} carousel={carousel} reduced={reduced}
                    agent={item} index={index} isActive={index === currentIndex} />
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
